import { useCallback, useEffect, useState } from "react";
import { Check, Clipboard, QrCode as QrIcon, SquareTerminal } from "lucide-react";
import { cn } from "@/lib/cn";
import { copyText } from "@/lib/clipboard";
import { TONE, type Tone } from "@/deck/tone";
import { Ar } from "@/deck/typography/Ar";
import type { PromptBlock } from "@/schema";
import { isUrlPrompt } from "@/schema/promptId";
import { usePromptQr } from "@/deck/promptQr";
import { STRINGS } from "@/strings/strings.gen";

const S = STRINGS.blocks.prompt;

/**
 * A prompt's text, laid out exactly as authored.
 *
 * One element per line, each `dir="auto"`, so an English instruction line runs
 * left to right and an Arabic one right to left inside the same prompt, while
 * the copy is still the authored string byte for byte. `whitespace-pre-wrap`
 * keeps the indents; an empty line keeps its height. Latin runs go through
 * `<Ar>` like every other line of copy (0.72em, never letter-spaced).
 */
export function PromptText({ text, className }: { text: string; className?: string }) {
  return (
    <div className={cn("whitespace-pre-wrap break-words", className)}>
      {text.split("\n").map((line, i) => (
        <div key={i} dir="auto">{line ? <Ar t={line} /> : " "}</div>
      ))}
    </div>
  );
}

type CopyState = "idle" | "ok" | "fail";

/** A copy button that says what happened, and says it for two seconds. A
 *  prompt that is one link says "copy the link", not "copy the command". */
export function CopyButton({ text, className }: { text: string; className?: string }) {
  const idle = isUrlPrompt(text) ? S.copy_link : S.copy;
  const [state, setState] = useState<CopyState>("idle");
  useEffect(() => {
    if (state === "idle") return;
    const id = window.setTimeout(() => setState("idle"), 2000);
    return () => window.clearTimeout(id);
  }, [state]);
  const copy = useCallback(async () => setState((await copyText(text)) ? "ok" : "fail"), [text]);

  return (
    <button
      type="button"
      onClick={copy}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold",
        state === "ok" ? "bg-success-surface text-success-ink" : "bg-accent text-accent-contrast",
        className,
      )}
    >
      {state === "ok" ? <Check aria-hidden className="h-4 w-4" /> : <Clipboard aria-hidden className="h-4 w-4" />}
      <Ar t={state === "ok" ? S.copied : state === "fail" ? S.failed : idle} />
    </button>
  );
}

/**
 * A ready-to-use prompt, as the slide's content.
 *
 * Set large, in a card that reads as "this is the thing to type", because it
 * is read from the back of a room. The same text reaches every phone with a
 * copy button (the display reports it with its position), so the button here
 * is for whoever watches on their own screen.
 */
export function Prompt({ b, tone }: { b: PromptBlock; tone: Tone }) {
  const t = TONE[tone];
  // Only the presenting screen offers the "scan to copy" QR (see promptQr.ts).
  const qr = usePromptQr();
  const qrId = qr?.idFor(b);
  return (
    <div className="flex flex-col gap-3">
      <figure className={cn("overflow-hidden rounded-2xl border bg-surface-sunken", t.border)}>
        <figcaption className="flex items-center justify-between gap-4 border-b border-border px-5 py-3">
          <span className={cn("inline-flex min-w-0 items-center gap-2 text-base font-semibold sm:text-lg", t.text)}>
            <SquareTerminal aria-hidden className="h-5 w-5 shrink-0" />
            {b.title && <span className="truncate"><Ar t={b.title} /></span>}
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {qr && qrId && (
              <button
                type="button" onClick={() => qr.open(qrId)} aria-label={S.qr_button} title={S.qr_button}
                className="grid h-9 w-9 place-items-center rounded-lg text-text-muted hover:bg-surface-hover hover:text-text"
              >
                <QrIcon aria-hidden className="h-5 w-5" />
              </button>
            )}
            <CopyButton text={b.text} className="shrink-0" />
          </span>
        </figcaption>
        <PromptText
          text={b.text}
          className="max-h-[52vh] overflow-y-auto px-6 py-5 text-xl leading-relaxed text-text sm:text-2xl lg:text-3xl"
        />
      </figure>
      {/* Outside the card on purpose: the card is what gets copied. */}
      {b.caption && <p className="text-center text-base text-text-muted sm:text-lg"><Ar t={b.caption} /></p>}
    </div>
  );
}
